// EmbedRuntimeContext — host-injected runtime config.
//
// The standalone shell loads deployment-specific values from
// `/config.json` before the app boots. Without an injection point,
// every workspace consumer of `@nurion/chat/embed` would inherit the
// standalone shell's config regardless of their own deployment.
//
// `EmbedRuntimeProvider` is that injection point: hosts that pass
// `consoleUrl` (and `homeserverUrl` — which the embed already takes as
// a prop on `EmbeddableChatProvider`) override the default values for
// the duration of the mount.
//
// Per-field laziness — the load-bearing rule:
//
//   Each field is resolved independently. A host that supplies
//   `homeserverUrl` but leaves `consoleUrl` undefined only triggers the
//   console-url envalid gate. That's what makes the embed bundle safe
//   to ship without a runtime config as long as the host always passes
//   the prop.
//
// Lookup order at the consumer:
//
//   1. `useEmbedRuntime()` — every chat-internal call site that needs
//      one of these values goes through this hook.
//   2. The hook reads from `EmbedRuntimeContext`. If a provider above
//      it set the field, that value wins.
//   3. If no provider is mounted (legacy paths, tests that forget the
//      wrapper), or a field is left `undefined`, the hook falls back
//      to the matching `getDefault…` per-field getter from `~/env`.
//      Standalone Tauri/web shells continue to work without any new
//      env vars.
//
// The provider holds a stable, memoised resolved object so consumers
// don't churn on every parent render.

import { createContext, useContext, useMemo, type ReactNode } from "react";

import { getDefaultConsoleUrl, getDefaultHomeserverUrl, getMeetOrigins } from "~/env";

/**
 * Resolved runtime-env values consumed inside the chat tree. Both
 * fields are always present after `useEmbedRuntime()` resolution; the
 * consumer never has to branch on `undefined`.
 */
export interface EmbedRuntimeEnv {
  /** Synapse homeserver URL the Matrix client points at. */
  homeserverUrl: string;
  /** Console origin used by the Space selector's create-space deep-link. */
  consoleUrl: string;
  /** Hostnames allowed for Matrix meeting join links. */
  meetOrigins: readonly string[];
}

/**
 * Per-field overrides a host app can pass through
 * `EmbeddableChatProviderProps`. `undefined` means "use the default".
 */
export interface EmbedRuntimeOverrides {
  homeserverUrl?: string;
  consoleUrl?: string;
  meetOrigins?: readonly string[];
}

const EmbedRuntimeContext = createContext<EmbedRuntimeEnv | null>(null);

/**
 * Read the resolved runtime env. Returns provider-supplied values
 * when an `EmbedRuntimeProvider` is mounted above; falls back to the
 * compile-time `import.meta.env.VITE_*` defaults otherwise.
 *
 * The fallback is intentional — legacy code paths (the standalone
 * Tauri/web shell, individual stories that don't wrap with the
 * provider, tests) keep working with the env-driven values. Hosts
 * that want explicit control mount `EmbedRuntimeProvider` (or use
 * `EmbeddableChatProvider`, which mounts it for them).
 *
 * Note: when no provider is mounted this calls every per-field gate
 * eagerly because the standalone shell wants every value present.
 * Embed code paths route through `EmbedRuntimeProvider` whose
 * resolution only invokes the gates for fields the host left
 * undefined.
 */
export function useEmbedRuntime(): EmbedRuntimeEnv {
  const ctx = useContext(EmbedRuntimeContext);
  if (ctx) return ctx;
  return {
    homeserverUrl: getDefaultHomeserverUrl(),
    consoleUrl: getDefaultConsoleUrl(),
    meetOrigins: getMeetOrigins(),
  };
}

interface EmbedRuntimeProviderProps {
  children: ReactNode;
  /** Optional per-field overrides. `undefined` resolves to the env
   *  default; explicit values win. */
  overrides?: EmbedRuntimeOverrides;
}

export function EmbedRuntimeProvider({ children, overrides }: EmbedRuntimeProviderProps) {
  // Memoise per-field, not per-`overrides`-identity, so a parent that
  // inlines `overrides={{ … }}` doesn't churn the context value on
  // every render. Each field is treated independently so an embed
  // bundle whose host passes `consoleUrl` as a prop never trips
  // console-url validation, even if no runtime config was loaded.
  const overrideHomeserverUrl = overrides?.homeserverUrl;
  const overrideConsoleUrl = overrides?.consoleUrl;
  const overrideMeetOrigins = overrides?.meetOrigins;
  const value = useMemo<EmbedRuntimeEnv>(
    () => ({
      homeserverUrl: overrideHomeserverUrl ?? getDefaultHomeserverUrl(),
      consoleUrl: overrideConsoleUrl ?? getDefaultConsoleUrl(),
      meetOrigins: overrideMeetOrigins ?? getMeetOrigins(),
    }),
    [overrideHomeserverUrl, overrideConsoleUrl, overrideMeetOrigins],
  );

  return <EmbedRuntimeContext.Provider value={value}>{children}</EmbedRuntimeContext.Provider>;
}

// Re-export the context so a host that wants direct access (Storybook
// decorators, downstream test harnesses that compose providers
// manually) can wire it up without going through the provider
// wrapper. Production chat code uses `useEmbedRuntime()`; this is
// part of the public surface to keep the module's seams legible.
export { EmbedRuntimeContext };
